import React from 'react'
import type { ReactNode } from 'react'

import type { Deal, LineItem } from '../types/deal'
import { useArchiveDealMutation } from '../hooks/useArchiveDealMutation'
import { useModal } from '../state/ModalProvider'
import { useFilters } from '../state/FilterProvider'
import {
  formatActivationLabel,
  getShipmentProgress,
  getUrgency,
  isDealArchived,
  isDealCanceled,
} from '../utils/dealUrgency'

interface Props {
  deal: Deal
  companyNameMap: Map<string, string>
  children?: ReactNode
}

function badgeClassFor(item: LineItem): string {
  if (item.type === 'hw') return 'badge-hw'
  if (item.type === 'sw') return 'badge-sw'
  return 'badge-otp'
}

export const DealRow: React.FunctionComponent<Props> = (props) => {
  const { deal, companyNameMap, children } = props
  const archiveMutation = useArchiveDealMutation()
  const { open } = useModal()
  const { isDealExpanded, toggleExpandDeal } = useFilters()

  const archived = isDealArchived(deal)
  const canceled = isDealCanceled(deal)
  const lineItems = deal.lineItems || []
  const progress = getShipmentProgress(deal)
  const fullyDelivered = !progress.hasShipments || progress.deliveredShip === progress.totalShip
  const urgency = getUrgency(deal.expirationDate)
  const expanded = isDealExpanded(deal.id)
  const companyResolved = companyNameMap.get(deal.companyId)
  const companyDisplay = companyResolved ?? deal.companyId
  const activationDisplay = formatActivationLabel(deal.activationDate)

  const rowClass = `deal-main-row${archived ? ' deal-archived' : ''}${canceled ? ' deal-canceled' : ''}`

  function onClickEdit(): void {
    open({ kind: 'edit-deal', isNew: false, dealId: deal.id })
  }

  function onClickArchive(): void {
    archiveMutation.mutate({ dealId: deal.id, restore: archived })
  }

  function onClickExpand(): void {
    toggleExpandDeal(deal.id)
  }

  return (
    <>
      <tr className={rowClass} data-tier={urgency.tier} data-deal-order={deal.orderNumber}>
        <td>
          <span
            className={`expand-toggle${expanded ? ' open' : ''}`}
            role="button"
            aria-label={expanded ? 'Collapse' : 'Expand'}
            onClick={onClickExpand}
          >
            ▶
          </span>
        </td>
        <td>
          <div
            className="deal-name"
            title={companyResolved ? undefined : 'Catalog loading…'}
          >
            {companyDisplay}
          </div>
          <div className="deal-order">{deal.orderNumber}</div>
          <div className="deal-quote">{deal.quoteNumber}</div>
          <div className="deal-rep">{deal.salesRepInitials ?? ''}</div>
        </td>
        <td>
          {lineItems.map((item, index) => (
            <div key={index} className="line-item-row">
              <span className={`badge ${badgeClassFor(item)}`}>{item.label}</span>
              <span>
                {item.productName} ×{item.qty}
              </span>
            </div>
          ))}
        </td>
        <td>
          {!fullyDelivered ? (
            <>
              <div className="date-main" style={{ color: '#6e6e73', fontWeight: 500 }}>
                Pending delivery
              </div>
              <div className="date-sub">
                {progress.deliveredShip} of {progress.totalShip} shipments delivered
              </div>
            </>
          ) : (
            <>
              <div className="date-main">{activationDisplay || '—'}</div>
              {canceled ? (
                <div className="urgency-badge tier-canceled">
                  Canceled
                  {urgency.days != null && urgency.days > 0
                    ? ` · was ${Math.max(1, Math.round(urgency.days / 30))} mo`
                    : ''}
                </div>
              ) : (
                <div className={`urgency-badge tier-${urgency.tier}`}>{urgency.label}</div>
              )}
            </>
          )}
        </td>
        <td>
          <span className="billing-text">{deal.billingCadence}</span>
        </td>
        <td>
          <div className="action-btns">
            <button type="button" className="action-btn" title="Edit" onClick={onClickEdit}>
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M11.5 2.5l2 2-8.5 8.5H3v-2l8.5-8.5z" />
              </svg>
            </button>
            <button
              type="button"
              className="action-btn"
              title={archived ? 'Unarchive deal' : 'Archive deal'}
              onClick={onClickArchive}
              disabled={archiveMutation.isLoading}
            >
              {archived ? (
                <svg
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 7v13a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V7" />
                  <path d="M21 3H3v4h18V3z" />
                  <path d="M9 14l3-3 3 3" />
                  <path d="M12 11v7" />
                </svg>
              ) : (
                <svg
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="3" width="18" height="4" rx="1" />
                  <path d="M5 7v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7" />
                  <path d="M10 12h4" />
                </svg>
              )}
            </button>
          </div>
        </td>
      </tr>
      {children}
    </>
  )
}
